// src/views/add/Add3.js

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchMeterBrands,
  fetchQnoms,
  fetchDiameters,
  createMeterReport,
  updateMeterReport,
  uploadMeterReportImage,
  addMeter,
} from "../../data/fetchData";
import Popup from "reactjs-popup";
import { FaTimesCircle } from "react-icons/fa";
import { useNotification } from "../../context/NotificationContext";
import { formatDecimal, normalizeDecimal } from "../../utils/formatters";

const TYPE_LABELS = {
  MAIN_GAUGE: "Põhiarvesti",
  SUB_GAUGE: "Alamarvesti",
  WATERING: "Kastmine",
};

export default function Add3() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [data, setData] = useState({});
  const [brandOptions, setBrandOptions] = useState([]);
  const [qnOptions, setQnOptions] = useState([]);
  const [diamOptions, setDiamOptions] = useState([]);

  useEffect(() => {
    // Load summary data
    const stored = localStorage.getItem("addData");
    if (stored) setData(JSON.parse(stored));

    // Load lookup tables
    fetchMeterBrands().then(setBrandOptions).catch(console.error);
    fetchQnoms().then(setQnOptions).catch(console.error);
    fetchDiameters().then(setDiamOptions).catch(console.error);
  }, []);

  const fmtDate = (iso) =>
    iso
      ? new Date(iso).toLocaleDateString("et-EE", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : "—";

  const actionLabel =
    data.workType === "Paigaldamine" ? "Lisamine" : "Eemaldamine";
  const typeLabel = TYPE_LABELS[data.meterType] || data.meterType || "—";

  const brandName = (() => {
    const id = Number(data.veemootjaMark);
    const b = brandOptions.find((x) => x.id === id);
    return b ? b.name : data.veemootjaMark || "—";
  })();

  const qNomLabel = (() => {
    const id = Number(data.qn);
    const q = qnOptions.find((x) => x.id === id);
    return q ? formatDecimal(q.q_nom) : data.qn || "—";
  })();

  const diamLabel = (() => {
    const id = Number(data.diameter);
    const d = diamOptions.find((x) => x.id === id);
    return d ? formatDecimal(d.diameter) : data.diameter || "—";
  })();

  const onConsoleLabel =
    data.meterIsOnConsole === "Jah"
      ? "Jah"
      : data.meterIsOnConsole === "Ei"
      ? "Ei"
      : "—";

  const handleBackEdit = () => navigate(-1);

  const handleSendToDatabase = async () => {
    try {
      const toDateOnly = (dt) => new Date(dt).toISOString().slice(0, 10);
      const toBool = (v) => v === true || v === "Jah";

      // Helper function to convert base64 to Blob (same as in Add1.js)
      const dataURLtoBlob = (dataURL) => {
        const [header, base64] = dataURL.split(",");
        const mime = header.match(/:(.*?);/)[1];
        const bin = atob(base64);
        const arr = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
        return new Blob([arr], { type: mime });
      };

      const payload = {
        date: new Date(data.workDate).toISOString(),
        last_certification_date: toDateOnly(data.viimaneTaatlemine),
        verification_date: toDateOnly(data.sealDate),
        sealing_date: toDateOnly(data.sealDate),
        meter_number: data.tehaseNumber,
        estate: data.estate,
        client: data.client,
        reading: Number(normalizeDecimal(data.arvestiNait)) || 0,
        meter_brand: Number(data.veemootjaMark),
        q_nom: Number(data.qn),
        type: data.meterType,
        diameter: Number(data.diameter),
        seal_number: data.sealNumber,
        on_console: toBool(data.meterIsOnConsole),
        meter_node_compliant:
          data.meterNodeCompliant === "Jah"
            ? true
            : data.meterNodeCompliant === "Ei"
            ? false
            : null,
        action: data.workType === "Paigaldamine" ? "add" : "remove",
        signature: data.signature,
        status: data.signature ? "SIGNED" : "CONFIRMED",
        notes: data.notes || null,
      };

      let result;

      if (data.id) {
        result = await updateMeterReport(data.id, payload);
        showSuccess("Aruanne uuendatud edukalt.");
      } else {
        result = await createMeterReport(payload);
        showSuccess("Aruanne salvestatud edukalt.");
      }

      // Now call the addition API for the physical meter operations (using dedicated API endpoint)
      if (result?.id && payload.action === "add") {
        try {
          const additionResult = await addMeter(
            result.id,
            Number(normalizeDecimal(data.arvestiNait)) || 0
          );

          showSuccess("Meter paigaldamine lõpetatud edukalt!");
        } catch (additionErr) {
          console.error("Addition API error:", additionErr);
          console.error("Error details:", additionErr.response?.data);

          // Provide more specific error messages
          if (additionErr.response?.status === 404) {
            showError("Aruanne ei leitud. Kontrollige andmeid.");
          } else if (additionErr.response?.status === 400) {
            const errorMsg =
              additionErr.response?.data?.error ||
              additionErr.response?.data?.detail ||
              "Vigased andmed";
            showError(`Vigased andmed: ${errorMsg}`);
          } else {
            showError(
              `Meter paigaldamine ebaõnnestus: ${
                additionErr.response?.data?.error || additionErr.message
              }`
            );
          }

          // STOP THE WORKFLOW - Do not continue with image upload or navigation if meter addition fails
          return;
        }
      } else if (payload.action === "remove") {
      }

      // Upload images if any exist
      if (data.images && data.images.length > 0 && result?.id) {
        const reportPk = result.id;
        let uploadIndex = 1;

        for (const src of data.images) {
          if (!src.startsWith("data:")) {
            // existing image (fetched for preview), skip it
            continue;
          }

          try {
            const blob = dataURLtoBlob(src);
            const filename = `report-${result.number}-${uploadIndex}.png`;
            await uploadMeterReportImage(reportPk, blob, filename);
            uploadIndex++;
          } catch (imgErr) {
            // Don't throw here - continue with other images
          }
        }
      }

      localStorage.removeItem("addData");
      navigate("/works");
    } catch (err) {
      console.error("Error saving meter report:", err);
      showError(
        `Andmete salvestamisel ilmnes viga: ${
          err.response?.data?.detail ||
          err.response?.data?.message ||
          err.message
        }`
      );
    }
  };

  return (
    <div className="max-w-4xl mx-auto mt-24 p-6 bg-white shadow-lg rounded-lg border border-gray-300">
      <div className="bg-primary text-white text-center py-4 rounded-t-lg">
        <h1 className="text-xl font-bold">Kokkuvõte</h1>
      </div>

      <table className="w-full table-auto">
        <tbody>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">Veemõõtja number</th>
            <td className="p-2 border-t border-gray-200">{data.tehaseNumber || "—"}</td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">Kuupäev</th>
            <td className="p-2 border-t border-gray-200">
              {fmtDate(data.workDate)}
            </td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">Aadress</th>
            <td className="p-2 border-t border-gray-200">
              {data.aadress || "—"}
            </td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">Lepingupartner</th>
            <td className="p-2 border-t border-gray-200">
              {data.client_name || "—"}
            </td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">Töö liik</th>
            <td className="p-2 border-t border-gray-200">{actionLabel}</td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">Tüüp</th>
            <td className="p-2 border-t border-gray-200">{typeLabel}</td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">
              Arvesti mark
            </th>
            <td className="p-2 border-t border-gray-200">{brandName}</td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">Qn/m³</th>
            <td className="p-2 border-t border-gray-200">{qNomLabel}</td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">Läbimõõt</th>
            <td className="p-2 border-t border-gray-200">{diamLabel}</td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">
              Arvesti näit
            </th>
            <td className="p-2 border-t border-gray-200">
              {formatDecimal(data.arvestiNait) || "—"}
            </td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">
              Viimane taatlemine
            </th>
            <td className="p-2 border-t border-gray-200">
              {fmtDate(data.viimaneTaatlemine)}
            </td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">
              Plommimise kuupäev
            </th>
            <td className="p-2 border-t border-gray-200">
              {fmtDate(data.sealDate)}
            </td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">
              Plommi number
            </th>
            <td className="p-2 border-t border-gray-200">
              {data.sealNumber || "—"}
            </td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">
              Konsoolil
            </th>
            <td className="p-2 border-t border-gray-200">{onConsoleLabel}</td>
          </tr>
          {data.notes && (
            <tr>
              <th className="p-2 text-left border-t border-gray-200">
                Märkused
              </th>
              <td className="p-2 border-t border-gray-200">{data.notes}</td>
            </tr>
          )}
          {/* allkiri */}
          <tr>
            <th className="p-2 text-left border-t border-gray-200">Allkiri</th>
            <td className="p-2 border-t border-gray-200">
              {data.signature ? (
                <Popup
                  modal
                  trigger={
                    <img
                      src={data.signature}
                      alt="Allkiri"
                      className="w-24 h-24 object-cover rounded-md cursor-pointer"
                    />
                  }
                  closeOnDocumentClick={false}
                >
                  {(close) => (
                    <div className="relative p-4">
                      <img
                        src={data.signature}
                        alt="Allkiri"
                        className="w-full h-auto max-w-3xl mx-auto"
                      />
                      <button
                        onClick={close}
                        className="absolute top-2 right-2 text-2xl"
                      >
                        <FaTimesCircle />
                      </button>
                    </div>
                  )}
                </Popup>
              ) : (
                <em>Pole allkirja</em>
              )}
            </td>
          </tr>
          {/* pildid */}
          <tr>
            <th className="p-2 text-left border-t border-gray-200">Pildid</th>
            <td className="p-2 border-t border-gray-200">
              <div className="flex space-x-4">
                {data.images?.map((img, idx) => (
                  <Popup
                    key={idx}
                    modal
                    trigger={
                      <img
                        src={img}
                        alt={`Pilt ${idx + 1}`}
                        className="w-24 h-24 object-cover rounded-md cursor-pointer"
                      />
                    }
                    closeOnDocumentClick={false}
                  >
                    {(close) => (
                      <div className="relative p-4">
                        <img
                          src={img}
                          alt={`Pilt ${idx + 1}`}
                          className="w-full h-auto max-w-3xl mx-auto"
                        />
                        <button
                          onClick={close}
                          className="absolute top-2 right-2 text-2xl"
                        >
                          <FaTimesCircle />
                        </button>
                      </div>
                    )}
                  </Popup>
                ))}
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div className="flex justify-between mt-6">
        <button
          onClick={handleBackEdit}
          className="px-4 py-2 bg-secondary text-white rounded-md hover:bg-secondary-hover"
        >
          Tagasi
        </button>
        <button
          onClick={handleSendToDatabase}
          className="bg-primary text-white py-3 px-6 rounded-md hover:bg-primary-hover"
        >
          Kinnita akt
        </button>
      </div>
    </div>
  );
}
